import React, { useRef } from 'react'
import { InputGroup, FormControl, Table, Alert, Button } from 'react-bootstrap'
import Fuse from 'fuse.js'
import { LineItemData } from 'interfaces/line-item'
import { Price } from 'interfaces/price'
import { StripeProduct } from 'interfaces/stripe-product'

interface SearchSelectTableProps<T> {
  title: string
  placeholder: string
  query: string
  setQuery: (query: string) => void
  searchOptions: Fuse.IFuseOptions<T>
  data: T[]
  selectedItem: T | null
  onSelect: (item: T) => void
  onClear: () => void
  renderRow: (item: T) => {
    key: string | number
    columns: React.ReactNode[]
  }
  columnHeaders: string[]
  renderSelectedItem: (item: T) => string
  autoFocus?: boolean
  isLoading?: boolean
}

const SearchSelectTable = <T,>({
  title,
  placeholder,
  query,
  setQuery,
  searchOptions,
  data,
  selectedItem,
  onSelect,
  onClear,
  renderRow,
  columnHeaders,
  renderSelectedItem,
  autoFocus,
  isLoading,
}: SearchSelectTableProps<T>): JSX.Element => {
  const searchRef = useRef<HTMLInputElement>(null)
  const fuse = new Fuse(data, { ...searchOptions, fieldNormWeight: 1 })
  const results = query ? fuse.search(query) : data.map((item) => ({ item }))

  return (
    <div className="mb-4">
      <h5>{title}</h5>
      <InputGroup className="mb-3">
        <FormControl
          ref={searchRef}
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus={autoFocus}
        />
      </InputGroup>
      {!selectedItem && (
        <div className="overflow-auto" style={{ maxHeight: '500px' }}>
          {isLoading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Table striped bordered hover responsive variant="dark">
              <thead>
                <tr>
                  {columnHeaders.map((header, i) => (
                    <th key={i}>{header}</th>
                  ))}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {results.map(({ item }) => {
                  const { key, columns } = renderRow(item)
                  return (
                    <tr key={key}>
                      {columns.map((col, i) => (
                        <td key={i}>{col}</td>
                      ))}
                      <td>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => onSelect(item)}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </div>
      )}
      {selectedItem && (
        <Alert
          variant="info"
          className="d-flex justify-content-between align-items-center"
        >
          <span>Selected: {renderSelectedItem(selectedItem)}</span>
          <Button variant="success" size="sm" onClick={onClear}>
            Change
          </Button>
        </Alert>
      )}
    </div>
  )
}

export default SearchSelectTable
